import React, { FC } from 'react';

import { Box, Icon } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useOffersUIContext } from 'components/offers/ui-refresh/offers-ui-context';
import { useFeatureEarningCalculationQuery } from 'generated/sanity-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import useMediaQuery from 'hooks/use-media-query';
import useRoutes from 'hooks/use-routes';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLoyalty } from 'state/loyalty/hooks/use-loyalty';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import { RedemptionMethod } from 'state/offers';
import { useServiceModeContext } from 'state/service-mode';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import { routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { SHOULD_DISPLAY_MOBILE_ICON } from './constants';
import { ErrorMessage } from './details-panel-components';
import { StyledRedemptionButton, StyledSignUpToRedeemLink } from './redemption-button.styled';
import { RestaurantRedemptionButton } from './restaurant-redemption-button';
import { IRedemptionButtonsProps } from './types';

export const RedemptionButtons: FC<IRedemptionButtonsProps> = ({
  onRedeemOffer,
  disableMobileOfferRedemptions,
  disableInRestaurantOfferRedemptions,
  redemptionErrorMessage,
  showSignUpToRedeem,
  showMobileRedemptionButton,
  showRestaurantRedemptionButton,
  restaurantRedemptionButtonUiType,
  noPossibleRedemption,
  offerConfigLoading,
}) => {
  const { formatMessage } = useIntl();
  const { navigate } = useLocationContext();
  const { getLocalizedRouteForPath } = useRoutes();
  const location = useLocation();
  const { setOriginLocation } = useAuthContext();
  const isDesktop = useMediaQuery('desktop');
  const enableMyCodeNewUI = useFlag(LaunchDarklyFlag.ENABLE_MY_CODE_NEW_UI);
  const enableCooldownToast = useFlag(LaunchDarklyFlag.ENABLE_COOLDOWN_TOAST_INFO);
  const { serviceMode } = useServiceModeContext();
  const {
    SwitchCartMethodDialog,
    shouldShowSwitchToStoreDialog,
    shouldShowSwitchToMobileDialog,
    openSwitchCartMethod,
  } = useSwitchCartMethodDialog();
  const { existEntryTypeInRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  const { loading: offersLoading } = useOffersUIContext();

  const { data: earningCalculationData } = useFeatureEarningCalculationQuery({
    variables: { id: 'earningCalculation' },
  });
  const { loyaltyUser } = useLoyalty();
  const diffInMin = getDifferenceToNow(
    DIFFERENCE_TIME_UNITS.MINUTES,
    loyaltyUser?.offerRedemptionAvailability?.availableAfter || ''
  );

  const showOfferCooldownToast = () => {
    //If the diffInMin is greater than 0 it means that the user already have an offer in course so we do nothing.
    if (!enableCooldownToast || diffInMin > 0 || !serviceMode) {
      return;
    }

    toast.default(
      formatMessage(
        { id: 'offersCooldownInfo' },
        {
          period: earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod,
        }
      )
    );
  };

  const setMobileRedemption = () => onRedeemOffer({ redemptionMethod: RedemptionMethod.mobile });
  const handleOnClickMobileRedemption = () => {
    if (shouldShowSwitchToMobileDialog) {
      openSwitchCartMethod({ onConfirm: setMobileRedemption });
    } else {
      setMobileRedemption();
    }
    showOfferCooldownToast();
  };

  const setRestaurantRedemption = () =>
    onRedeemOffer({ redemptionMethod: RedemptionMethod.restaurant });

  const [ConfirmAddOfferDialog, openConfirmAddOfferDialog] = useDialogModal({
    onConfirm: setRestaurantRedemption,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Select new offer for in-restaurant redemption cart',
  });

  const handleOnClickRestaurantRedemption = () => {
    if (shouldShowSwitchToStoreDialog) {
      openSwitchCartMethod({ onConfirm: setRestaurantRedemption });
    } else if (existEntryTypeInRestaurantRedemptionCart(ICartEntryType.LEGACY_OFFER)) {
      openConfirmAddOfferDialog();
    } else {
      setRestaurantRedemption();
    }
    showOfferCooldownToast();
  };

  const routeToSignUp = (event: React.SyntheticEvent) => {
    event.preventDefault();
    setOriginLocation(location.pathname);
    navigate(getLocalizedRouteForPath(routes.signUp));
  };

  if (noPossibleRedemption) {
    return <ErrorMessage data-testid="redemption-error">{redemptionErrorMessage}</ErrorMessage>;
  }

  if (showSignUpToRedeem) {
    return (
      <Box margin={isDesktop ? '1.2rem 0 0' : '1rem 0 0'}>
        <StyledSignUpToRedeemLink
          data-testid="sign-up-to-redeem"
          to={routes.signUp}
          onClick={routeToSignUp}
        >
          {formatMessage({ id: 'signUpToRedeem' })}
        </StyledSignUpToRedeemLink>
      </Box>
    );
  }

  return (
    <>
      {redemptionErrorMessage && <ErrorMessage>{redemptionErrorMessage}</ErrorMessage>}

      {showMobileRedemptionButton && (
        <Box margin={isDesktop ? '1.2rem 0 0' : '1rem 0 0'}>
          <StyledRedemptionButton
            data-testid="mobile-redemption-button"
            onClick={handleOnClickMobileRedemption}
            disabled={disableMobileOfferRedemptions || offersLoading}
            aria-label={formatMessage({ id: 'addMobileOrder' })}
            isLoading={offersLoading || offerConfigLoading}
            startIcon={
              SHOULD_DISPLAY_MOBILE_ICON ? (
                <Icon
                  data-testid="mobile-redemption-icon"
                  icon={'mobile'}
                  color={'white'}
                  width="20px"
                  aria-hidden
                />
              ) : null
            }
          >
            {formatMessage({ id: 'addMobileOrder' })}
          </StyledRedemptionButton>
        </Box>
      )}

      {showRestaurantRedemptionButton && (
        <RestaurantRedemptionButton
          onClickRestaurantRedemption={handleOnClickRestaurantRedemption}
          restaurantRedemptionButtonTitle={formatMessage({
            id: enableMyCodeNewUI ? 'redeemAtRestaurant' : 'redeemInRestaurant',
          })}
          restaurantRedemptionButtonUiType={restaurantRedemptionButtonUiType}
          disabled={disableInRestaurantOfferRedemptions}
          isLoading={offersLoading || offerConfigLoading}
        />
      )}
      <SwitchCartMethodDialog />
      <ConfirmAddOfferDialog
        heading={formatMessage({ id: 'oneOfferPerOrder' })}
        body={formatMessage({ id: 'replaceOffer' })}
        confirmLabel={formatMessage({ id: 'yes' })}
        cancelLabel={formatMessage({ id: 'no' })}
      />
    </>
  );
};
